import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCountUp } from "@/components/admin/CountUp";

/** Tiny inline sparkline – a smooth path + soft fill, no library. `values` may be all zeros (renders a flat baseline). */
export const Sparkline = ({ values = [], className, stroke = "hsl(var(--sol))", height = 30, width = 92, testId }) => {
  const n = values.length;
  if (!n) return null;
  const max = Math.max(1, ...values);
  const pad = 3;
  const xs = values.map((_, i) => pad + (i * (width - pad * 2)) / Math.max(1, n - 1));
  const ys = values.map((v) => height - pad - (v / max) * (height - pad * 2));
  let d = `M ${xs[0]} ${ys[0]}`;
  for (let i = 1; i < n; i += 1) {
    const cx = (xs[i - 1] + xs[i]) / 2;
    d += ` C ${cx} ${ys[i - 1]}, ${cx} ${ys[i]}, ${xs[i]} ${ys[i]}`;
  }
  const area = `${d} L ${xs[n - 1]} ${height} L ${xs[0]} ${height} Z`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className={cn("overflow-visible", className)} aria-hidden="true" data-testid={testId}>
      <path d={area} fill={stroke} opacity="0.12" />
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={xs[n - 1]} cy={ys[n - 1]} r="2.5" fill={stroke} />
    </svg>
  );
};

/** Metrics side by side, each marked by one hairline on the left – no boxes. 2-up on mobile, `cols` from lg/xl. */
export const StatGroup = ({ children, cols = 4, className, testId, style }) => (
  <div className={cn("grid grid-cols-2 gap-x-4 gap-y-6 sm:gap-x-6", cols === 3 && "lg:grid-cols-3", cols === 4 && "xl:grid-cols-4", className)} data-testid={testId} style={style}>
    {children}
  </div>
);

/**
 * Metric: eyebrow label · big display value (+ sparkline) · hint. `attention` turns the hairline sol,
 * `to` makes the whole metric a link. `icon` is accepted for backwards compatibility but not rendered.
 */
export const StatTile = ({ label, value, hint, to, series, trend, attention = false, className, testId, children }) => {
  const animated = useCountUp(typeof value === "number" ? value : Number(value) || 0);
  const shown = typeof value === "number" || /^\d+$/.test(String(value ?? "")) ? animated.toLocaleString("nb-NO") : value;
  const body = (
    <>
      <p className="flex min-w-0 items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {attention && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sol" aria-hidden="true" />}
        <span className="truncate">{label}</span>
      </p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="min-w-0 truncate font-display text-[28px] font-semibold leading-none tracking-[-0.03em] tabular sm:text-[32px]" data-testid={testId ? `${testId}-value` : undefined}>{shown}</p>
        {series && <Sparkline values={series} className="hidden shrink-0 sm:block" testId={testId ? `${testId}-sparkline` : undefined} />}
      </div>
      {(hint || trend) && (
        <p className="mt-2 flex min-w-0 items-center gap-1.5 truncate text-[12px] text-muted-foreground">
          {trend && <span className={cn("shrink-0 rounded px-1 py-px text-[11px] font-medium tabular", trend.startsWith("+") ? "bg-[hsl(var(--status-confirmed-bg))] text-status-confirmed" : "bg-secondary text-muted-foreground")}>{trend}</span>}
          <span className="min-w-0 truncate">{hint}</span>
        </p>
      )}
      {children}
      {to && <ArrowUpRight className="absolute right-0 top-0 h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover/tile:opacity-100" aria-hidden="true" />}
    </>
  );
  const cls = cn("metric group/tile relative block min-w-0 py-0.5 transition-colors", attention && "metric-attention", to && "hover:[border-left-color:hsl(var(--foreground)/0.5)] focus-visible:outline-none focus-visible:[border-left-color:hsl(var(--foreground))]", className);
  return to ? <Link to={to} className={cls} data-testid={testId}>{body}</Link> : <div className={cls} data-testid={testId}>{body}</div>;
};
